import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Beneficio, Plan } from '../../types/Plan';
import { CATEGORIAS, GRUPOS } from '../../data/planes';
import { COLOR_BANDA, agruparPorGrupo } from './planesHelpers';
import PlanIdentidad from './PlanIdentidad';
import BeneficioChip from './BeneficioChip';
import { Card, Button, IconSwap, easeStandard } from '../ui';
import { ChevronDownIcon, ChevronUpIcon } from '../icons';

export interface PlanCardProps {
  plan: Plan;
}

const GRUPOS_CON_CATEGORIAS = agruparPorGrupo(CATEGORIAS, GRUPOS);

interface SeccionListaProps {
  titulo: string;
  items: string[];
}

const SeccionLista: React.FC<SeccionListaProps> = ({ titulo, items }) => {
  if (items.length === 0) return null;
  return (
    <div>
      <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {titulo}
      </h4>
      <ul className="list-disc space-y-0.5 ps-5">
        {items.map((item) => (
          <li key={item} className="text-sm text-slate-600">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

interface TitularTileProps {
  etiqueta: string;
  beneficio: Beneficio;
}

/**
 * Franja de titulares: los tres números que más se consultan, en grande, para
 * que se lean de un vistazo. Si no hay descuento (sea 'no' o 'no-especificado')
 * se muestra un guión discreto en vez de un porcentaje.
 *
 * El relleno ya separa el bloque del fondo de la tarjeta, así que no lleva
 * borde: sería una segunda línea diciendo lo mismo.
 */
const TitularTile: React.FC<TitularTileProps> = ({ etiqueta, beneficio }) => {
  const tieneValor = beneficio.estado === 'si' && beneficio.pct !== null;
  return (
    <div
      className="flex min-h-[5.25rem] flex-col items-center justify-center gap-0.5 rounded-xl bg-slate-50 px-2 py-3 text-center"
      role="img"
      aria-label={`${etiqueta}: ${beneficio.resumen}`}
    >
      {tieneValor ? (
        <span aria-hidden="true" className="flex items-baseline justify-center gap-1 whitespace-nowrap">
          {beneficio.esTope && <span className="text-xs font-semibold text-slate-400">hasta</span>}
          <span className="font-extrabold leading-none tabular-nums text-gappex-dark text-3xl sm:text-4xl">
            {beneficio.pct}%
          </span>
        </span>
      ) : (
        <span aria-hidden="true" className="text-xl font-bold leading-none text-slate-400 sm:text-2xl">
          N/A
        </span>
      )}
      <span aria-hidden="true" className="mt-1 text-[11px] uppercase tracking-wide text-slate-500">
        {etiqueta}
      </span>
    </div>
  );
};

const PlanCard: React.FC<PlanCardProps> = ({ plan }) => {
  const [expandido, setExpandido] = useState(false);
  const detalleId = `plan-detalle-${plan.id}`;

  const hayDetalle = plan.requisitos.length > 0 || plan.exclusiones.length > 0 || plan.notas.length > 0;

  return (
    <Card as="section" interactive className="h-full p-5">
      <PlanIdentidad plan={plan} />

      <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
        <TitularTile etiqueta="Ambulatorio" beneficio={plan.beneficios.consultas} />
        <TitularTile etiqueta="Internación" beneficio={plan.beneficios.habitacion} />
        <TitularTile etiqueta="Farmacia" beneficio={plan.beneficios.farmacia} />
      </div>

      <div className="mt-4 space-y-3">
        {GRUPOS_CON_CATEGORIAS.map(({ grupo, alcance, categorias }) => (
          <div key={grupo} className="overflow-hidden rounded-xl">
            <div
              className="flex items-baseline justify-between gap-2 px-3 py-1.5"
              style={{ backgroundColor: COLOR_BANDA }}
            >
              <span className="text-[11px] font-bold uppercase tracking-wider text-white">{grupo}</span>
              <span className="truncate text-right text-[10px] font-normal normal-case tracking-normal text-white/70">
                {alcance}
              </span>
            </div>
            <div className="divide-y divide-slate-100">
              {categorias.map((cat, idx) => {
                const beneficio = plan.beneficios[cat.id];
                const mostrarSublabel =
                  cat.sublabel && (beneficio.estado === 'si' || beneficio.estado === 'incluido');
                return (
                  <div
                    key={cat.id}
                    className={`flex items-center justify-between gap-3 px-3 py-2 ${
                      idx % 2 === 1 ? 'bg-slate-50/70' : 'bg-white'
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm text-slate-700">{cat.label}</p>
                      {mostrarSublabel && (
                        <p className="truncate text-xs text-slate-400">{cat.sublabel}</p>
                      )}
                    </div>
                    <BeneficioChip beneficio={beneficio} label={cat.label} size="sm" />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {hayDetalle && (
        <>
          <div className="mt-4 flex justify-end border-t border-slate-100 pt-3">
            <Button
              variant="ghost"
              onClick={() => setExpandido((v) => !v)}
              aria-expanded={expandido}
              aria-controls={detalleId}
              className="text-gappex-primary hover:bg-blue-50 hover:text-blue-700"
              icon={
                <IconSwap swapKey={expandido ? 'up' : 'down'}>
                  {expandido ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </IconSwap>
              }
            >
              {expandido ? 'Ocultar detalle' : 'Ver detalle'}
            </Button>
          </div>

          <AnimatePresence initial={false}>
            {expandido && (
              <motion.div
                id={detalleId}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.25, ease: easeStandard } }}
                exit={{ opacity: 0, y: -8, transition: { duration: 0.15, ease: 'easeOut' } }}
                className="space-y-4 border-t border-slate-100 pt-4"
              >
                <SeccionLista titulo="Requisitos de ingreso" items={plan.requisitos} />
                <SeccionLista titulo="Exclusiones del plan" items={plan.exclusiones} />
                <SeccionLista titulo="A tener en cuenta" items={plan.notas} />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </Card>
  );
};

export default PlanCard;
